import { useCallback } from 'react';
import Key, { JumpPosition } from '@shared/classes/key';
import VIRTUAL_KEYBOARD_ACTIONS from '@shared/types/enums/virtualKeyboardActions';
import TextKey from '@components/elements/keys/textKey';
import EmptyKey from '@components/atoms/container/keyEmpty';

function useVirtualKeyboard(MAX_COLS: number) {
  const getKeys = useCallback(
    (list: string[], gaps: Key[], prevStride = 0, addEmptyKeys = true) => {
      const lastIndex = list.length - 1 + prevStride;
      const stride = addEmptyKeys
        ? list.length + (MAX_COLS - (list.length % MAX_COLS)) + prevStride
        : list.length;
      const keys = list.map((item, listIndex) => {
        // Key index based on stride
        const index = listIndex + prevStride;

        // Key jump interactions
        const isGap = (index: number) => {
          return gaps.find(
            (item) =>
              item.spaces === index ||
              ((item?.spaces as number[])?.length &&
                (item.spaces as number[])?.includes(index))
          );
        };

        const left = index % MAX_COLS !== 0 ? index - 1 : -1;
        let right: number | undefined = index + 1;
        let down: number | undefined = index + MAX_COLS;
        let up: number | undefined = index - MAX_COLS;

        right = isGap(right) || index % MAX_COLS === MAX_COLS - 1 ? -1 : right;
        down = isGap(down || -1) ? index + MAX_COLS * 2 : down;
        up = isGap(up) ? index - MAX_COLS * 2 : up;

        // Create current key's jump object
        const jump = new JumpPosition(left, right, up, down);
        // Create and return key object
        return new Key({
          action: VIRTUAL_KEYBOARD_ACTIONS.WRITE,
          component: <TextKey char={item} index={index} key={index} />,
          jump,
          spaces: index,
        });
      });

      return { keys, lastIndex, stride };
    },
    []
  );

  const getGaps = useCallback(
    (chars: string[][], addEmptyKeys = true) =>
      chars.reduce(
        (data, list) => {
          if (!addEmptyKeys) return data;
          const start = list.length + data.stride;
          const end = Math.round(start / MAX_COLS) * MAX_COLS;
          for (let index = start; index < end; index++) {
            data.keys.push(
              new Key({
                action: VIRTUAL_KEYBOARD_ACTIONS.CUSTOM,
                component: <EmptyKey key={index} />,
                spaces: index,
                customAction: () => null,
              })
            );
          }
          data.stride += end;
          return data;
        },
        { keys: [], stride: 0 } as { keys: Key[]; stride: number }
      ).keys,
    []
  );

  return { getKeys, getGaps };
}

export default useVirtualKeyboard;
